<script setup lang="ts" generic="TItem extends string | Record<string, any> | null | undefined">
// https://reka-ui.com/docs/components/select

const props = withDefaults(
  defineProps<{
    label?: string
    name: string
    items: TItem[]
    itemTitle?: string
    itemValue?: string
    placeholder?: string
    // This is a poor thing to mimic anchor positioning
    menuDirection?: `left` | `default`
  }>(),
  {
    itemTitle: `title`,
    itemValue: `value`,
    menuDirection: `default`,
  },
);

const modelValue = defineModel<TItem>();

defineSlots<{
  selection: (props: { item: TItem | undefined }) => unknown
  item: (props: { item: TItem }) => unknown
}>();

const displayedValue = computed(() => {
  if (modelValue.value == null) return undefined;
  if (typeof modelValue.value === `object` && props.itemTitle in modelValue.value) return modelValue.value[props.itemTitle];
  return modelValue.value;
});

const renderedItems = computed(() => {
  return props.items
    .filter(item => item != null)
    .map((item) => {
      return {
        title: typeof item === `object` && props.itemTitle in item ? item[props.itemTitle] : item,
        value: item,
      };
    });
});

// NOTE: not using v-text in `SelectValue`was preventing Placeholder display
</script>

<template>
  <!-- eslint-disable vue/no-v-text-v-html-on-component -->
  <div>
    <IadLabel
      :html-for="name"
      :text="label"
    />
    <SelectRoot
      v-model="modelValue"
      class="relative"
    >
      <SelectTrigger
        class="inline-flex items-center relative border rounded-md border-neutral-30 focus-within:border-secondary px-s py-xs w-full bg-transparent
        min-h-[42px]
        group/trigger data-[placeholder]:text-neutral"
      >
        <SelectValue
          :placeholder
          v-text="displayedValue"
        />
        <Icon
          name="iad-icon:caret-down"
          class="ms-auto group-data-[state=open]/trigger:rotate-180"
        />
      </SelectTrigger>
      <SelectContent
        class="max-h-60 overflow-auto overscroll-contain w-[--reka-select-trigger-width]
          rounded-md bg-white ring-1 ring-neutral-60 shadow-popover
          will-change-[transform,opacity] data-[state=open]:animate-slideDownAndFade empty:hidden z-10"
        :side-offset="5"
        position="popper"
      >
        <SelectViewport class="divide-y divide-neutral-80">
          <SelectItem
            v-for="(item, index) in renderedItems"
            :key="index"
            class="hover:bg-secondary-70 px-m py-s flex justify-between items-center"
            :value="item.value"
          >
            <SelectItemText>
              {{ item.title }}
            </SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectRoot>
  </div>
</template>
